/**
 * MIQBaselineReport.js
 * 
 * Comprehensive Day 30 Baseline Report UI
 * Shows for Genius Plaite users only
 * 
 * Sections:
 * 1. Cover + Snapshot
 * 2. What MIQ Learned About You (Coach Insights)
 * 3. Habit Signals Dashboard
 * 4. Nutrition Pattern Analysis
 * 5. SmartGoals + Medical Sensitivity
 * 6. Next 30 Days Plan
 * 7. MIQ Confidence Breakdown
 * 8. Baseline Locked In + CTA
 */

import React, { useState, useEffect } from 'react';
import { 
  X, Brain, Target, TrendingUp, Droplets, Activity, 
  CheckCircle, AlertCircle, Clock, Utensils, Heart,
  Calendar, Zap, ChevronRight, Award, Sparkles,
  Shield, BookOpen, RefreshCw
} from 'lucide-react';
import { brand } from '../../styles/brand';
import { track } from '../../utils/analytics';

const { colors, gradients, typography, radius, shadows, spacing } = brand;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ═══════════════════════════════════════════════════════════════════════════════
// HELPER COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════

const ScoreRing = ({ score, label, size = 120 }) => {
  const strokeWidth = 8;
  const normalizedRadius = (size - strokeWidth) / 2;
  const circumference = normalizedRadius * 2 * Math.PI;
  const strokeDashoffset = circumference - (score / 100) * circumference;
  
  const getScoreColor = (s) => {
    if (s >= 70) return '#38ef7d';
    if (s >= 50) return '#667eea';
    if (s >= 30) return '#FFD700';
    return '#ff6b6b';
  };

  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        {/* Background circle */}
        <circle
          stroke="rgba(255,255,255,0.1)"
          fill="transparent"
          strokeWidth={strokeWidth}
          r={normalizedRadius}
          cx={size / 2}
          cy={size / 2}
        />
        {/* Progress circle */}
        <circle
          stroke={getScoreColor(score)}
          fill="transparent"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={`${circumference} ${circumference}`}
          style={{ strokeDashoffset, transition: 'stroke-dashoffset 1s ease' }}
          r={normalizedRadius}
          cx={size / 2}
          cy={size / 2}
        />
      </svg>
      <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: '28px', fontWeight: '700', color: colors.text }}>{score}</div>
        <div style={{ fontSize: '11px', color: colors.textSecondary }}>{label}</div>
      </div>
    </div>
  );
};

const Chip = ({ icon: Icon, label, value, active }) => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 10px',
    background: active ? 'rgba(102, 126, 234, 0.2)' : 'rgba(255,255,255,0.05)',
    border: `1px solid ${active ? 'rgba(102, 126, 234, 0.4)' : 'rgba(255,255,255,0.1)'}`,
    borderRadius: '20px',
    fontSize: '12px',
    color: active ? colors.text : colors.textSecondary
  }}>
    {Icon && <Icon size={14} />}
    <span>{label}</span>
    {value !== undefined && <strong style={{ color: colors.text }}>{value}</strong>}
  </div>
);

const HabitSignalCard = ({ signal }) => {
  const getStatusColor = (status) => {
    if (status === 'Strong') return '#38ef7d';
    if (status === 'Developing') return '#FFD700';
    return '#ff6b6b';
  };

  const getStatusIcon = (status) => {
    if (status === 'Strong') return CheckCircle;
    if (status === 'Developing') return Clock;
    return AlertCircle;
  };

  const StatusIcon = getStatusIcon(signal.status);
  const statusColor = getStatusColor(signal.status);

  return (
    <div style={{
      background: 'rgba(255,255,255,0.03)',
      border: `1px solid rgba(255,255,255,0.08)`,
      borderRadius: radius.md,
      padding: spacing.s3,
      borderLeft: `3px solid ${statusColor}`
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '16px' }}>{signal.icon}</span>
          <span style={{ fontSize: '13px', fontWeight: '600', color: colors.text }}>{signal.name}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <StatusIcon size={14} color={statusColor} />
          <span style={{ fontSize: '12px', color: statusColor, fontWeight: '500' }}>{signal.status}</span>
        </div>
      </div>
      <p style={{ fontSize: '12px', color: colors.textSecondary, margin: 0, lineHeight: 1.4 }}>
        {signal.insight}
      </p>
    </div>
  );
};

const ObjectiveCard = ({ objective }) => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: spacing.s3,
    background: 'rgba(102, 126, 234, 0.1)',
    borderRadius: radius.md,
    border: '1px solid rgba(102, 126, 234, 0.2)'
  }}>
    <span style={{ fontSize: '20px' }}>{objective.icon}</span>
    <div>
      <div style={{ fontSize: '13px', fontWeight: '600', color: colors.text }}>{objective.title}</div>
      <div style={{ fontSize: '11px', color: colors.textSecondary }}>{objective.description}</div>
    </div>
  </div>
);

const ConfidenceFactorRow = ({ factor }) => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 0',
    borderBottom: '1px solid rgba(255,255,255,0.05)'
  }}>
    <span style={{ fontSize: '13px', color: colors.text }}>{factor.name}</span>
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <span style={{
        fontSize: '11px',
        padding: '2px 8px',
        borderRadius: '10px',
        background: factor.status === 'Complete' || factor.status === 'Strong' || factor.status === 'Active' || factor.status === 'Tracked'
          ? 'rgba(56, 239, 125, 0.2)'
          : factor.status === 'Growing' || factor.status === 'Developing' || factor.status === 'Partial'
            ? 'rgba(255, 215, 0, 0.2)'
            : 'rgba(255, 107, 107, 0.2)',
        color: factor.status === 'Complete' || factor.status === 'Strong' || factor.status === 'Active' || factor.status === 'Tracked'
          ? '#38ef7d'
          : factor.status === 'Growing' || factor.status === 'Developing' || factor.status === 'Partial'
            ? '#FFD700'
            : '#ff6b6b'
      }}>
        {factor.status}
      </span>
      {factor.next_action && (
        <ChevronRight size={14} color={colors.textSecondary} />
      )}
    </div>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION WRAPPER
// ═══════════════════════════════════════════════════════════════════════════════

const Section = ({ title, icon: Icon, iconColor = '#667eea', children }) => (
  <div style={{
    background: colors.panel,
    border: `1px solid ${colors.stroke}`,
    borderRadius: radius.lg,
    padding: spacing.s4,
    marginBottom: spacing.s3
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: spacing.s3 }}>
      <Icon size={18} color={iconColor} />
      <span style={{ fontSize: '15px', fontWeight: '600', color: colors.text }}>{title}</span>
    </div>
    {children}
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

const MIQBaselineReport = ({ isOpen, onClose, userId, onNavigateToWeeklyBriefs }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [baseline, setBaseline] = useState(null);

  useEffect(() => {
    if (isOpen && userId) {
      fetchBaseline();
    }
  }, [isOpen, userId]);

  const fetchBaseline = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${BACKEND_URL}/api/miq/baseline/${userId}`);
      const data = await response.json();
      if (data.success) {
        setBaseline(data.baseline);
        track('miq_baseline_report_viewed', { source: data.source });
      } else {
        setError(data.detail || 'Failed to load baseline report');
      }
    } catch (err) {
      console.error('[MIQBaselineReport] Error:', err);
      setError('Failed to load baseline report');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'linear-gradient(180deg, #0d0d14 0%, #1a1a2e 100%)',
      zIndex: 10002,
      overflowY: 'auto'
    }}>
      {/* Header */}
      <div style={{
        position: 'sticky',
        top: 0,
        background: 'linear-gradient(180deg, rgba(13, 13, 20, 0.98) 0%, rgba(26, 26, 46, 0.95) 100%)',
        backdropFilter: 'blur(10px)',
        padding: spacing.s4,
        borderBottom: `1px solid ${colors.stroke}`,
        zIndex: 10
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '12px',
              background: gradients.primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Brain size={22} color="white" />
            </div>
            <div>
              <h1 style={{ fontSize: '18px', fontWeight: '700', color: colors.text, margin: 0 }}>
                MIQ Intelligence Report
              </h1>
              <p style={{ fontSize: '12px', color: colors.textSecondary, margin: '2px 0 0 0' }}>
                Current Report
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.1)',
              border: 'none',
              borderRadius: '50%',
              width: '36px',
              height: '36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: colors.text
            }}
          >
            <X size={20} />
          </button>
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: spacing.s4 }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <RefreshCw size={32} color={colors.primary} style={{ animation: 'spin 1s linear infinite' }} />
            <p style={{ color: colors.textSecondary, marginTop: '16px' }}>Generating your baseline report...</p>
          </div>
        ) : error ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <AlertCircle size={32} color="#ff6b6b" />
            <p style={{ color: colors.textSecondary, marginTop: '16px' }}>{error}</p>
            <button
              onClick={fetchBaseline}
              style={{
                marginTop: '16px',
                padding: '10px 20px',
                background: gradients.primary,
                border: 'none',
                borderRadius: radius.md,
                color: 'white',
                cursor: 'pointer'
              }}
            >
              Retry
            </button>
          </div>
        ) : baseline ? (
          <>
            {/* 1. Cover + Snapshot */}
            <div style={{
              background: `linear-gradient(135deg, ${colors.panel2} 0%, rgba(102, 126, 234, 0.15) 100%)`,
              border: `2px solid ${colors.primary}`,
              borderRadius: radius.xl,
              padding: spacing.s5,
              marginBottom: spacing.s4,
              textAlign: 'center'
            }}>
              <ScoreRing score={baseline.cover?.baseline_score || 0} label={baseline.cover?.score_label || 'Calculating'} />
              
              <h2 style={{ fontSize: '20px', fontWeight: '700', color: colors.text, margin: '16px 0 8px 0' }}>
                Your MIQ Intelligence Report
              </h2>
              <p style={{ fontSize: '13px', color: colors.textSecondary, margin: '0 0 20px 0' }}>
                {baseline.baseline_locked?.report_period || 'Based on 30 days of data'}
              </p>
              
              {/* Chips */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center' }}>
                <Chip icon={Calendar} label="Days Logged" value={baseline.cover?.chips?.days_logged} active={baseline.cover?.chips?.days_logged >= 20} />
                <Chip icon={Utensils} label="Meals" value={baseline.cover?.chips?.meals_logged} active={baseline.cover?.chips?.meals_logged >= 30} />
                <Chip icon={Droplets} label="Hydration Days" value={baseline.cover?.chips?.hydration_days_hit} active={baseline.cover?.chips?.hydration_days_hit >= 15} />
                <Chip icon={Activity} label="Activity Days" value={baseline.cover?.chips?.activity_days_logged} active={baseline.cover?.chips?.activity_days_logged >= 10} />
                {baseline.cover?.chips?.smartgoals_completed && <Chip icon={Target} label="SmartGoals" active />}
                {baseline.cover?.chips?.smart_timer_enabled && <Chip icon={Clock} label="IF Timer" active />}
              </div>
            </div>

            {/* 2. What MIQ Learned About You */}
            <Section title="What MIQ Learned About You" icon={Brain} iconColor="#9333ea">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {baseline.coach_insights?.map((insight, i) => (
                  <div key={i} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    padding: '12px',
                    background: 'rgba(147, 51, 234, 0.1)',
                    borderRadius: radius.md,
                    borderLeft: '3px solid #9333ea'
                  }}>
                    <Sparkles size={16} color="#9333ea" style={{ flexShrink: 0, marginTop: '2px' }} />
                    <p style={{ fontSize: '13px', color: colors.text, margin: 0, lineHeight: 1.5 }}>{insight}</p>
                  </div>
                ))}
              </div>
            </Section>

            {/* 3. Habit Signals Dashboard */}
            <Section title="Habit Signals Dashboard" icon={TrendingUp} iconColor="#38ef7d">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {baseline.habit_signals?.map((signal, i) => (
                  <HabitSignalCard key={i} signal={signal} />
                ))}
              </div>
            </Section>

            {/* 4. Nutrition Pattern Analysis */}
            <Section title="Nutrition Patterns" icon={Utensils} iconColor="#FF9800">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                <div style={{ background: 'rgba(255,255,255,0.03)', padding: '12px', borderRadius: radius.md }}>
                  <div style={{ fontSize: '11px', color: colors.textSecondary }}>Daily Avg</div>
                  <div style={{ fontSize: '18px', fontWeight: '700', color: colors.text }}>
                    {baseline.nutrition_patterns?.daily_calorie_avg || 0} kcal
                  </div>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.03)', padding: '12px', borderRadius: radius.md }}>
                  <div style={{ fontSize: '11px', color: colors.textSecondary }}>Protein Trend</div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: 
                    baseline.nutrition_patterns?.protein_trend === 'On Track' ? '#38ef7d' : '#FFD700' 
                  }}>
                    {baseline.nutrition_patterns?.protein_trend || 'Tracking'}
                  </div>
                </div>
              </div>
              
              {baseline.nutrition_patterns?.common_repeat_meals?.length > 0 && (
                <div style={{ marginTop: '12px' }}>
                  <div style={{ fontSize: '12px', color: colors.textSecondary, marginBottom: '8px' }}>Your Go-To Meals</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {baseline.nutrition_patterns.common_repeat_meals.map((meal, i) => (
                      <span key={i} style={{
                        padding: '4px 10px',
                        background: 'rgba(102, 126, 234, 0.15)',
                        borderRadius: '12px',
                        fontSize: '12px',
                        color: colors.text
                      }}>
                        {meal}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </Section>

            {/* 5. SmartGoals + Medical Sensitivity */}
            {baseline.smartgoals_summary?.completed && (
              <Section title="Health Profile Active" icon={Heart} iconColor="#ff6b6b">
                {baseline.smartgoals_summary.medical_conditions?.length > 0 && (
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontSize: '12px', color: colors.textSecondary, marginBottom: '6px' }}>Health Conditions</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {baseline.smartgoals_summary.medical_conditions.map((c, i) => (
                        <span key={i} style={{
                          padding: '4px 10px',
                          background: 'rgba(255, 107, 107, 0.15)',
                          borderRadius: '12px',
                          fontSize: '12px',
                          color: '#ff6b6b'
                        }}>
                          {c}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                <div style={{ fontSize: '12px', color: colors.textSecondary, marginBottom: '8px' }}>Priority Nutrition Rules</div>
                <ul style={{ margin: 0, paddingLeft: '20px', color: colors.text, fontSize: '13px' }}>
                  {baseline.smartgoals_summary.priority_nutrition_rules?.map((rule, i) => (
                    <li key={i} style={{ marginBottom: '6px' }}>{rule}</li>
                  ))}
                </ul>
              </Section>
            )}

            {/* 6. Next 30 Days Plan */}
            <Section title="Next 30 Days Plan" icon={Target} iconColor="#667eea">
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '12px', color: colors.textSecondary, marginBottom: '10px' }}>Your Objectives</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {baseline.next_30_plan?.objectives?.map((obj, i) => (
                    <ObjectiveCard key={i} objective={obj} />
                  ))}
                </div>
              </div>
              
              {baseline.next_30_plan?.weekly_ritual && (
                <div style={{
                  background: 'rgba(255, 215, 0, 0.1)',
                  border: '1px solid rgba(255, 215, 0, 0.3)',
                  borderRadius: radius.md,
                  padding: spacing.s3,
                  marginBottom: '16px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                    <span style={{ fontSize: '16px' }}>{baseline.next_30_plan.weekly_ritual.icon}</span>
                    <span style={{ fontSize: '13px', fontWeight: '600', color: colors.text }}>
                      {baseline.next_30_plan.weekly_ritual.title}
                    </span>
                  </div>
                  <p style={{ fontSize: '12px', color: colors.textSecondary, margin: 0 }}>
                    {baseline.next_30_plan.weekly_ritual.description}
                  </p>
                </div>
              )}
              
              {baseline.next_30_plan?.smart_swaps?.length > 0 && (
                <div>
                  <div style={{ fontSize: '12px', color: colors.textSecondary, marginBottom: '8px' }}>Default Smart Swaps</div>
                  {baseline.next_30_plan.smart_swaps.map((swap, i) => (
                    <div key={i} style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '10px 0',
                      borderBottom: i < baseline.next_30_plan.smart_swaps.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none'
                    }}>
                      <div>
                        <span style={{ fontSize: '12px', color: colors.textSecondary }}>When craving </span>
                        <span style={{ fontSize: '13px', color: colors.text, fontWeight: '500' }}>{swap.craving}</span>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '12px', color: '#38ef7d' }}>→ {swap.swap}</div>
                        <div style={{ fontSize: '11px', color: colors.textSecondary }}>{swap.savings}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Section>

            {/* Month-over-Month Progress (only shows when previous report exists) */}
            {baseline.month_over_month?.has_previous && (
              <Section title="Month-over-Month Progress" icon={TrendingUp} iconColor="#38ef7d">
                {/* Score comparison */}
                <div data-testid="mom-score-comparison" style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '16px',
                  padding: '12px',
                  marginBottom: '14px',
                  borderRadius: '10px',
                  background: baseline.month_over_month.score_delta >= 0 
                    ? 'rgba(56, 239, 125, 0.08)' 
                    : 'rgba(244, 67, 54, 0.08)',
                  border: `1px solid ${baseline.month_over_month.score_delta >= 0 ? 'rgba(56,239,125,0.2)' : 'rgba(244,67,54,0.2)'}`
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '11px', color: colors.textSecondary }}>Previous</div>
                    <div style={{ fontSize: '22px', fontWeight: '700', color: colors.textSecondary }}>{baseline.month_over_month.previous_score}</div>
                  </div>
                  <div style={{ fontSize: '18px', color: baseline.month_over_month.score_delta >= 0 ? '#38ef7d' : '#f44336' }}>→</div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '11px', color: colors.textSecondary }}>Current</div>
                    <div style={{ fontSize: '22px', fontWeight: '700', color: colors.text }}>{baseline.month_over_month.current_score}</div>
                  </div>
                  <div style={{
                    padding: '4px 10px',
                    borderRadius: '12px',
                    fontSize: '13px',
                    fontWeight: '700',
                    background: baseline.month_over_month.score_delta >= 0 ? 'rgba(56,239,125,0.15)' : 'rgba(244,67,54,0.15)',
                    color: baseline.month_over_month.score_delta >= 0 ? '#38ef7d' : '#f44336'
                  }}>
                    {baseline.month_over_month.score_delta > 0 ? '+' : ''}{baseline.month_over_month.score_delta}
                  </div>
                </div>

                {/* Summary */}
                <p style={{ fontSize: '13px', color: colors.text, lineHeight: 1.5, marginBottom: '14px' }}>
                  {baseline.month_over_month.summary}
                </p>

                {/* Habit changes */}
                {baseline.month_over_month.habit_changes?.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {baseline.month_over_month.habit_changes.map((habit, i) => (
                      <div key={i} data-testid={`mom-habit-${i}`} style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '10px 12px',
                        borderRadius: '8px',
                        background: habit.direction === 'improved' ? 'rgba(56,239,125,0.05)' : 'rgba(244,67,54,0.05)',
                        border: `1px solid ${habit.direction === 'improved' ? 'rgba(56,239,125,0.12)' : 'rgba(244,67,54,0.12)'}`
                      }}>
                        <span style={{ fontSize: '13px', color: colors.text }}>{habit.name}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '12px', color: colors.textSecondary }}>{habit.previous}%</span>
                          <span style={{ fontSize: '11px', color: colors.textSecondary }}>→</span>
                          <span style={{ fontSize: '12px', fontWeight: '600', color: colors.text }}>{habit.current}%</span>
                          <span style={{
                            fontSize: '11px',
                            fontWeight: '600',
                            color: habit.direction === 'improved' ? '#38ef7d' : '#f44336'
                          }}>
                            {habit.delta > 0 ? '+' : ''}{habit.delta}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Meals comparison */}
                {baseline.month_over_month.meals_comparison && (
                  <div style={{ 
                    marginTop: '12px', 
                    fontSize: '12px', 
                    color: colors.textSecondary, 
                    textAlign: 'center' 
                  }}>
                    Meals logged: {baseline.month_over_month.meals_comparison.previous} → {baseline.month_over_month.meals_comparison.current}
                  </div>
                )}
              </Section>
            )}

            {/* 7. MIQ Confidence Breakdown */}
            <Section title="MIQ Confidence Breakdown" icon={Award} iconColor="#FFD700">
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '16px'
              }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  background: gradients.primary,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: shadows.glow
                }}>
                  <span style={{ fontSize: '24px', fontWeight: '700', color: 'white' }}>
                    {baseline.confidence_breakdown?.overall_confidence || 0}%
                  </span>
                </div>
              </div>
              
              <div>
                {baseline.confidence_breakdown?.factors?.map((factor, i) => (
                  <ConfidenceFactorRow key={i} factor={factor} />
                ))}
              </div>
            </Section>

            {/* 8. Baseline Locked In */}
            <div style={{
              background: gradients.primary,
              borderRadius: radius.lg,
              padding: spacing.s5,
              textAlign: 'center',
              marginBottom: spacing.s4
            }}>
              <Zap size={32} color="white" style={{ marginBottom: '12px' }} />
              <h3 style={{ fontSize: '18px', fontWeight: '700', color: 'white', margin: '0 0 8px 0' }}>
                Baseline Locked In
              </h3>
              <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.85)', margin: '0 0 16px 0' }}>
                {baseline.baseline_locked?.message}
              </p>
              <button
                data-testid="continue-to-weekly-briefs-btn"
                onClick={() => {
                  onClose();
                  if (onNavigateToWeeklyBriefs) onNavigateToWeeklyBriefs();
                }}
                style={{
                  padding: '12px 24px',
                  background: 'rgba(255,255,255,0.2)',
                  border: '1px solid rgba(255,255,255,0.3)',
                  borderRadius: radius.md,
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Continue to Weekly Briefs
              </button>
            </div>

            {/* Disclaimer */}
            <div style={{
              background: 'rgba(255,255,255,0.02)',
              border: `1px solid ${colors.stroke}`,
              borderRadius: radius.md,
              padding: spacing.s3,
              display: 'flex',
              alignItems: 'flex-start',
              gap: '10px'
            }}>
              <Shield size={14} color={colors.textSecondary} style={{ flexShrink: 0, marginTop: '2px' }} />
              <p style={{ fontSize: '11px', color: colors.textSecondary, margin: 0, lineHeight: 1.4 }}>
                MIQ provides personalized pattern insights and does not diagnose or treat medical conditions.
                Always consult healthcare professionals for medical advice.
              </p>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default MIQBaselineReport;
